/**
 * Compose the ledger rows one event produced into the ONE entry it was (#2147).
 *
 * A single Shopify webhook writes TWO `resource_audit_logs` rows — the
 * projection upsert, then the enrichment reconcile — and both carry the same
 * `event_id`. The timeline renders one row as one entry, so an operator saw a
 * single edit as two entries that appear to cancel out: the upsert reports
 * `metafields` removed, the reconcile reports them restored, and the field that
 * actually moved is buried between them.
 *
 * The fix is a DISPLAY-layer one on purpose. The ledger keeps both rows — it is
 * the audit trail, and collapsing it in SQL would destroy evidence — and paging
 * keeps counting raw rows (`AuditTimeline`'s `getNextPageParam`), so nothing
 * upstream of the renderer changes. The accepted consequence is that a group
 * straddling a page boundary shows unmerged until the next page lands.
 */
import type { ResourceAuditTimelineItem } from '@/services';
import { structuralJsonDiff } from './json-diff';

/**
 * The RENDERER's own notion of "nothing moved", borrowed rather than
 * reimplemented.
 *
 * `ChangeSummary` and `ChangeRow` decide whether a JSON column changed by
 * asking `structuralJsonDiff` for its `changedCount`, which also absorbs a
 * key-order-only difference (real: the backend compares the serialized
 * column). Reusing it is what keeps "this merge discarded the field" and "the
 * view would not have marked it changed" from ever disagreeing. A
 * hand-rolled deep-equal would be a second, weaker predicate; `lodash.isEqual`
 * is blacklisted in this repo.
 */
const valuesEquivalent = (a: unknown, b: unknown): boolean => {
  const tree = structuralJsonDiff(a, b);
  return tree ? tree.changedCount === 0 : Object.is(a, b);
};

/**
 * `(resourceType, resourceId, eventId)`, NOT `eventId` alone: one prod event
 * spans two different resources (four rows), and keying on the event would
 * merge unrelated records into one entry. JSON-encoded so no separator can
 * collide with an id.
 */
const groupKeyOf = (entry: ResourceAuditTimelineItem): string =>
  JSON.stringify([entry.resourceType, entry.resourceId, entry.eventId]);

/**
 * One output position. A `rows` array of length 1, or one holding anything
 * other than an `update`, is passthrough — and passthrough hands back the
 * ORIGINAL objects (see `mergeGroup`).
 */
interface Slot {
  rows: ResourceAuditTimelineItem[];
}

function mergeGroup(rows: ResourceAuditTimelineItem[]): ResourceAuditTimelineItem[] {
  // Passthrough, and the reason the identity invariant holds: nothing is
  // rebuilt on this path. `create` and `delete` rows carry snapshots instead of
  // a field diff, and `restore` is a different statement about the record;
  // composing any of them with an update would invent a change nobody made.
  if (rows.length < 2 || rows.some((row) => row.action !== 'update')) return rows;

  // A COPY: the caller's array and the group's input order both have to
  // survive, since passthrough above returns the very same array.
  // `created_at` is UTC ISO-8601, so lexicographic order is chronological, and
  // it has no tie-break — `Array.prototype.sort` is stable, which is what keeps
  // rows stamped at the same instant in arrival order.
  const ordered = [...rows].sort((a, b) =>
    a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0,
  );

  // Per field: the FIRST row that mentions it gives `oldValue`, the LAST row
  // that mentions it gives `newValue`. "That mentions it" is the whole point —
  // a four-row group can touch a field only in its middle rows, and A→B→C→D
  // still has to read as A→D.
  const oldValues = new Map<string, unknown>();
  const newValues = new Map<string, unknown>();
  const fieldOrder: string[] = [];
  for (const row of ordered) {
    for (const change of row.changes) {
      if (!oldValues.has(change.field)) {
        oldValues.set(change.field, change.oldValue);
        fieldOrder.push(change.field);
      }
      newValues.set(change.field, change.newValue);
    }
  }

  // Indexed access rather than importing `ResourceAuditFieldChange`: the
  // services barrel deliberately does not re-export it, and the entry type
  // already names it.
  const changes: ResourceAuditTimelineItem['changes'] = [];
  for (const field of fieldOrder) {
    const oldValue = oldValues.get(field);
    const newValue = newValues.get(field);
    // The double write's signature: removed by one row, restored identically
    // by the next. It never moved, so it is not a change.
    if (valuesEquivalent(oldValue, newValue)) continue;
    changes.push({ field, oldValue, newValue });
  }

  // Every field composed back to where it started: the pair of rows describes
  // no net edit at all, and rendering it would put the noise back on screen.
  if (changes.length === 0) return [];

  const earliest = ordered[0];
  return [
    {
      id: earliest.id,
      resourceType: earliest.resourceType,
      resourceId: earliest.resourceId,
      action: earliest.action,
      actorType: earliest.actorType,
      actorId: earliest.actorId,
      actorLabel: earliest.actorLabel,
      source: earliest.source,
      eventId: earliest.eventId,
      createdAt: earliest.createdAt,
      changes,
      // Already null across an all-`update` group; stated rather than copied so
      // the merged entry cannot inherit half a snapshot.
      newValues: null,
      oldValues: null,
    },
  ];
}

/**
 * The loaded rows as the operator should read them: one entry per event.
 *
 * Pure, and identity-preserving where it does not merge — `ChangeSummary`
 * memoises on `entry.changes[0]` and runs once per rendered row, so rebuilding
 * a passthrough entry would re-parse a multi-KB payload on every render.
 *
 * Order is the input's: the list arrives descending by `created_at`, a group's
 * rows are adjacent in it, and the merged entry takes the group's first-seen
 * position. Nothing is re-sorted.
 */
export function mergeAuditEntries(
  entries: readonly ResourceAuditTimelineItem[],
): ResourceAuditTimelineItem[] {
  const slots: Slot[] = [];
  const byKey = new Map<string, Slot>();

  for (const entry of entries) {
    // Loose nullish check: a row with no event is its own group, and so is a
    // shape that omits the field. `null` must never become a group key, or a
    // page of manual edits on one record would collapse into a single entry.
    if (entry.eventId == null) {
      slots.push({ rows: [entry] });
      continue;
    }
    const key = groupKeyOf(entry);
    const existing = byKey.get(key);
    if (existing) {
      existing.rows.push(entry);
      continue;
    }
    const slot: Slot = { rows: [entry] };
    byKey.set(key, slot);
    slots.push(slot);
  }

  return slots.flatMap((slot) => mergeGroup(slot.rows));
}
